import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';

const About = () => {
  const title = 'About TradeRadar | Global Trade Risk Intelligence Platform';
  const description =
    'Learn about TradeRadar, a data-driven global trade risk intelligence platform that tracks chokepoints, tariffs, sanctions, and policy shocks to help users understand how geopolitics affects supply chains.';

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <SEOHead
        title={title}
        description={description}
        path="/about"
        keywords="about TradeRadar, global trade risk intelligence, Strait of Hormuz platform"
      />

      <header className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-on-surface mb-3">
          About TradeRadar
        </h1>
        <p className="text-sm text-slate-600 dark:text-on-surface-variant">
          Last updated: March 2026
        </p>
      </header>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">
          Mission
        </h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed">
          TradeRadar is a global trade risk intelligence platform focused on how geopolitics,
          chokepoints, and policy decisions affect supply chains, prices, and real economies. The
          goal is to turn scattered news and data into structured, decision‑ready insight for people
          who need to understand trade risk – from analysts and researchers to operators and
          policymakers.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">
          What the Platform Provides
        </h2>
        <ul className="text-sm text-slate-700 dark:text-on-surface-variant space-y-2 list-disc list-inside">
          <li>Chokepoint hubs for locations such as the Strait of Hormuz.</li>
          <li>
            Dashboards that summarise trade policy moves, sanctions, and regulatory changes across
            countries and sectors.
          </li>
          <li>
            Scenario tools that help users think through how disruptions might affect flows and
            prices.
          </li>
          <li>
            Structured navigation between statistics, timelines, and qualitative analysis, so every
            page adds depth rather than repeating the same text.
          </li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">
          How Content Is Created
        </h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed mb-2">
          Content on TradeRadar is based on publicly available information, historical data,
          established research, and carefully framed scenario analysis. Where exact figures are not
          available or may vary across sources, the platform uses ranges and clearly caveated
          approximations instead of single precise numbers.
        </p>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed">
          The focus is on explaining mechanisms – how risk travels from an incident or policy change
          to trade flows, freight rates, or macroeconomic outcomes – rather than offering trading
          calls or short‑term price forecasts.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">
          Research, methodology & editorial policy
        </h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed mb-2">
          Beyond dashboards, we publish{' '}
          <Link to="/insights" className="text-primary-600 dark:text-primary hover:underline">
            methodology and long-form explainers
          </Link>{' '}
          so users can interpret scores and chokepoint scenarios in context.{' '}
          <Link to="/editorial-standards" className="text-primary-600 dark:text-primary hover:underline">
            Editorial standards
          </Link>{' '}
          describe sourcing, independence from advertisers, and how we handle corrections.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">
          Independence and Compliance
        </h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed mb-2">
          TradeRadar is an independent information platform. It does not execute trades, offer
          brokerage services, or manage client assets. Pages that touch on market outcomes include
          clear disclaimers, emphasising that all scenarios are illustrative and should not be used
          as sole inputs to investment or policy decisions.
        </p>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed">
          The site is designed with advertising and analytics policies in mind, including Google
          AdSense guidelines. Content avoids sensationalism and focuses on sober, analytical
          treatment of events.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">
          Who the Platform Is For
        </h2>
        <ul className="text-sm text-slate-700 dark:text-on-surface-variant space-y-2 list-disc list-inside">
          <li>Trade, energy, and policy analysts who need structured context for chokepoint risk.</li>
          <li>
            Logistics, shipping, and supply‑chain teams who want to understand how incidents may
            affect routing and cost.
          </li>
          <li>
            Students, researchers, and journalists looking for a clear starting point on complex
            trade‑risk topics.
          </li>
        </ul>
      </section>
    </article>
  );
};

export default About;

